import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePortfolioData } from '../components/data/PortfolioData';
import SidebarPageLayout from '../components/layout/SidebarPageLayout';
import PortfolioSpikeMap from '../components/graphs/PortfolioSpikeMap';
import WeatherTooltip from '../components/layout/WeatherTooltip';
import StatusBar from '../components/graphs/StatusBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faInfoCircle, faMapLocationDot, faScrewdriverWrench, faTable } from '@fortawesome/free-solid-svg-icons';
import { useSessionData } from "../components/data/SessionData";
import { createPortfolioDateFormatter } from '../utils/dateTime';
import { buildNavigation } from '../util/routeNavigation';
import { NAV_PLACEMENTS } from '../routes/navigationConstants';
import { toSlug } from '../util/strings';
import { displayTemperature, temperatureSymbol } from '../util/temperature';
import { useTemperatureUnit } from '../util/useTemperatureUnit';

function SiteLinksMenu({ plantName, siteLinks, open, onToggle, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  return (
    <div className="site-links-menu" ref={menuRef}>
      <button
        type="button"
        aria-label={`${plantName} site links`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        <FontAwesomeIcon icon={faScrewdriverWrench} />
      </button>
      {open && (
        <div className="site-links-menu__panel" role="menu">
          <h3>{plantName}</h3>
          <ul>
            {siteLinks.map((item) => (
              <li key={item.path}>
                <Link to={item.path} role="menuitem" onClick={onClose}>
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Portfolio() {
  const temperatureUnit = useTemperatureUnit();
  const { data } = usePortfolioData();
  const { plants } = useSessionData();
  const [openSiteMenuId, setOpenSiteMenuId] = useState(null);

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub - Portfolio";
  }, []);

  // only display data from active plants
  const filteredData = useMemo(() => {
    return data.filter((plant) => plants.includes(plant.plant_name));
  }, [data, plants]);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <div id="portfolio" className="panel p-3">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faBriefcase} /> PORTFOLIO</span>
            </div>
            <div className="right d-none align-items-stretch gap-2">
              <button><FontAwesomeIcon icon={faMapLocationDot} /></button> <button><FontAwesomeIcon icon={faTable} /></button>
            </div>
          </div>
          <div className="body">
            <table className="table">
              <thead>
                <tr align="center">
                  <td>&nbsp;</td>
                  <td>Site</td>
                  <td>Location</td>
                  <td>Plant Type</td>
                  <td>Plant Power<br/> MW AC</td>
                  <td>POI Max<br/> MW AC</td>
                  <td>Solar Power<br/> MW DC</td>
                  <td>Solar Rated Capacity<br/> MW DC</td>
                  <td>Irradiance<br/> W/m2</td>
                  <td>Temp<br/> {temperatureSymbol(temperatureUnit)}</td>
                  <td>Inverters</td>
                  <td>Generation<br/> MWh</td>
                  <td>Availability</td>
                  <td>Capacity Factor</td>
                  <td>State of Charge</td>
                  <td>Performance</td>
                  <td>Last Updated</td>
                  <td>&nbsp;</td>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(({ id, weather_condition, wind, dashboard, plant_name, location, config, plant_power, poi_max_power, solar_power, solar_rated_capacity, irradiance, bom_temperature, generation, capacity, availability_pct, soc, performance, temp, inverters_online, inverters_total, last_updated, timezone }) => {
                  const dateFormatter = createPortfolioDateFormatter(timezone);
                  const siteLinks = buildNavigation(NAV_PLACEMENTS.SITE, {
                    params: { plantName: toSlug(plant_name) },
                  }).filter((item) => item.label !== 'DASHBOARD');
                  return (
                  <tr align="center" key={id}>
                    <td>
                      <WeatherTooltip position="right" temp={temp} weather_condition={weather_condition} wind={wind} irradiance={irradiance} bom_temperature={bom_temperature}>
                        <button><FontAwesomeIcon icon={faInfoCircle} /></button>
                      </WeatherTooltip>
                    </td>
                    <td><Link to={`${dashboard}`}><strong>{plant_name}</strong></Link></td>
                    <td>{location}</td>
                    <td>{config}</td>
                    <td>{plant_power}</td>
                    <td>{poi_max_power}</td>
                    <td>{solar_power}</td>
                    <td>{solar_rated_capacity}</td>
                    <td>{irradiance ?? '—'}</td>
                    <td>{temp != null ? displayTemperature(temp, temperatureUnit) : '—'}</td>
                    <td>{inverters_online}/{inverters_total}</td>
                    <td>{generation}</td>
                    <td><StatusBar value={Number(availability_pct)} type="portfolioPercent" /></td>
                    <td><StatusBar value={Number(capacity)} type="capacity" /></td>
                    <td><StatusBar value={Number(soc)} type="soc" /></td>
                    <td><StatusBar value={Number(performance)} type="performance" /></td>
                    <td>{dateFormatter.formatUtc(last_updated)}</td>
                    <td>
                      <SiteLinksMenu
                        plantName={plant_name}
                        siteLinks={siteLinks}
                        open={openSiteMenuId === id}
                        onToggle={() => setOpenSiteMenuId((currentId) => (currentId === id ? null : id))}
                        onClose={() => setOpenSiteMenuId((currentId) => (currentId === id ? null : currentId))}
                      />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <PortfolioSpikeMap width={900} height={600} plantData={filteredData} />
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Portfolio;
